import React, { useState, useRef, lazy, Suspense, useEffect } from "react";
import {
  FaMicrophone,
  FaVideo,
  FaImage,
  FaPoll,
  FaAddressBook,
  FaCalendarAlt,
  FaFileAlt,
  FaLaughBeam,
  FaSmile,
  FaEdit,
  FaTrash,
  FaEye,
} from "react-icons/fa";
import EmojiPicker from "emoji-picker-react";
import "./Mensagem.css";

const AudioPlayer = ({ src }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    setDuration(audioRef.current.duration);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#dcf8c6",
        borderRadius: 8,
        padding: "8px 12px",
        maxWidth: 300,
        boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
      }}
    >
      {/* Ícone de microfone */}
      <FaMicrophone style={{ color: "#51a197", fontSize: 20 }} />

      {/* Botão de play/pause */}
      <button
        onClick={togglePlayPause}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontSize: 18,
          color: "#51a197",
        }}
      >
        {isPlaying ? "⏸️" : "▶️"}
      </button>

      {/* Barra de progresso */}
      <div style={{ flex: 1, height: 4, background: "#ccc", borderRadius: 2 }}>
        <div
          style={{
            width: `${(currentTime / duration) * 100}%`,
            height: "100%",
            background: "#51a197",
            borderRadius: 2,
          }}
        ></div>
      </div>

      {/* Tempo atual e duração */}
      <span style={{ fontSize: 12, color: "#555" }}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>

      {/* Elemento de áudio oculto */}
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        style={{ display: "none" }}
      />
    </div>
  );
};

const Enquete = lazy(() => import("./Enquete")); // Lazy load do componente Enquete
const Contato = lazy(() => import("./Contato")); // Lazy load do componente Contato
import Evento from "./Evento";

const Mensagem = (props) => {
  const {
    mensagens,
    setMensagens,
    mensagem,
    setMensagem,
    notifyColor,
    notifyIconStyle,
    isMobile, // Recebendo isMobile como prop
    mencionarTodos,
    setMencionarTodos,
    envioTemporario,
    setEnvioTemporario,
    velocidade,
    setVelocidade,
    whatsappBg,
    onValidacaoMensagem, // Nova prop para informar a validação
  } = props;

  const [editando, setEditando] = useState(null);
  const [modalAudioAberto, setModalAudioAberto] = useState(false);
  const [modalEnqueteAberto, setModalEnqueteAberto] = useState(false);
  const [modalContatoAberto, setModalContatoAberto] = useState(false);
  const [modalEventoAberto, setModalEventoAberto] = useState(false);
  const [gravandoAudio, setGravandoAudio] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [audioErro, setAudioErro] = useState("");
  const [gravador, setGravador] = useState(null);
  const [tempoGravacao, setTempoGravacao] = useState(0);
  const timerRef = useRef(null);
  const [mostrarEmojis, setMostrarEmojis] = useState(false);
  const [imagemUrl, setImagemUrl] = useState(null);

  // Função para iniciar a gravação de áudio
  const iniciarGravacao = async () => {
    setAudioErro("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new window.MediaRecorder(stream);
      let chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/mp3" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setGravador(null);
        clearInterval(timerRef.current);
      };
      setGravador(mediaRecorder);
      setAudioBlob(null);
      setAudioUrl(null);
      setTempoGravacao(0);
      setGravandoAudio(true);
      mediaRecorder.start();
      timerRef.current = setInterval(() => {
        setTempoGravacao((t) => t + 1);
      }, 1000);
    } catch (err) {
      setAudioErro("Não foi possível acessar o microfone.");
      console.error(err);
    }
  };

  // Função para parar a gravação de áudio
  const pararGravacao = () => {
    if (gravador) gravador.stop();
    setGravandoAudio(false);
    clearInterval(timerRef.current);
  };

  // Função para enviar o áudio gravado
  const handleEnviarAudio = () => {
    if (audioBlob) {
      setMensagens((prevMensagens) => [
        ...prevMensagens,
        { tipo: "audio", url: audioUrl, visualizacoes: 10, reproducoes: 5 }, // Exemplo de valores de teste
      ]);
      setAudioBlob(null);
      setAudioUrl(null);
      setGravandoAudio(false);
      setModalAudioAberto(false);
      setTempoGravacao(0);
    }
  };

  // Função para fechar o modal de gravação de áudio
  const fecharModalAudio = () => {
    setModalAudioAberto(false);
    setGravandoAudio(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioErro("");
    setTempoGravacao(0);
    clearInterval(timerRef.current);
  };

  // Função para excluir uma mensagem
  const handleExcluirMensagem = (index) => {
    console.log("Índice a ser excluído:", index);
    console.log("Mensagens antes da exclusão:", mensagens);
    setMensagens(mensagens.filter((_, idx) => idx !== index));
    console.log("Mensagens após a exclusão:", mensagens);
  };

  // Função para editar uma mensagem
  const handleEditarMensagem = (index) => {
    console.log("Índice a ser editado:", index);
    console.log("Mensagem a ser editada:", mensagens[index]);
    setMensagem(mensagens[index].conteudo); // Carrega apenas o conteúdo da mensagem no campo de texto
    setEditando(index); // Define o índice da mensagem que está sendo editada
  };

  // Função para salvar a edição da mensagem
  const handleSalvarEdicao = () => {
    if (editando !== null) {
      const mensagensAtualizadas = mensagens.map((msg, idx) =>
        idx === editando
          ? { ...msg, conteudo: mensagem } // Atualiza apenas o conteúdo da mensagem editada
          : msg
      );
      setMensagens(mensagensAtualizadas); // Atualiza o estado com as mensagens editadas
      setEditando(null); // Reseta o estado de edição
      setMensagem(""); // Limpa o campo de texto
    }
  };

  const renderComFormatacao = (texto) => {
    if (!texto || typeof texto !== "string") return texto; // Retorna o valor original se não for uma string

    const partes = texto.split(/(\*.*?\*|_.*?_|~.*?~)/g); // Divide o texto em partes com base nos padrões de formatação

    return partes.map((parte, index) => {
      if (/^\*.*\*$/.test(parte)) {
        // Texto em negrito (*texto*)
        return <strong key={index}>{parte.slice(1, -1)}</strong>;
      } else if (/^_.*_$/.test(parte)) {
        // Texto em itálico (_texto_)
        return <em key={index}>{parte.slice(1, -1)}</em>;
      } else if (/^~.*~$/.test(parte)) {
        // Texto tachado (~texto~)
        return <s key={index}>{parte.slice(1, -1)}</s>;
      } else {
        // Texto normal
        return parte;
      }
    });
  };

  // Função para enviar a enquete
  const handleEnviarEnquete = (enquete) => {
    setMensagens((prevMensagens) => [
      ...prevMensagens,
      {
        tipo: "enquete",
        conteudo: {
          pergunta: enquete.pergunta,
          opcoes: enquete.opcoes.map((opcao) => ({ texto: opcao, votos: 0 })), // Inicializa os votos com 0
        },
      },
    ]);
  };

  // Função para votar em uma opção da enquete
  const handleVotar = (msgIdx, opcaoIdx) => {
    setMensagens((prevMensagens) =>
      prevMensagens.map((msg, idx) => {
        if (idx === msgIdx && msg.tipo === "enquete") {
          const totalVotos = msg.conteudo.opcoes.reduce((sum, opcao) => sum + opcao.votos, 0) + 1;
          const novasOpcoes = msg.conteudo.opcoes.map((opcao, index) => {
            if (index === opcaoIdx) {
              return { ...opcao, votos: opcao.votos + 1 };
            }
            return opcao;
          });
          return {
            ...msg,
            conteudo: {
              ...msg.conteudo,
              opcoes: novasOpcoes,
              totalVotos,
            },
          };
        }
        return msg;
      })
    );
  };

  const incrementarVisualizacoes = (index) => {
    setMensagens((prevMensagens) =>
      prevMensagens.map((msg, idx) =>
        idx === index ? { ...msg, visualizacoes: msg.visualizacoes + 1 } : msg
      )
    );
  };

  const calcularPorcentagem = (parcial, total) => {
    if (total === 0) return 0;
    return Math.round((parcial / total) * 100);
  };

  // Função para enviar o contato
  const handleEnviarContato = (contato) => {
    setMensagens((prevMensagens) => [
      ...prevMensagens,
      { tipo: "contato", conteudo: contato }, // Adiciona o contato como uma nova mensagem
    ]);
  };

  // Função para enviar o evento
  const handleEnviarEvento = (evento) => {
    setMensagens((prevMensagens) => [
      ...prevMensagens,
      { tipo: "evento", conteudo: evento }, // Adiciona o evento como uma nova mensagem
    ]);
  };

  // Verifica se os campos estão preenchidos e informa ao componente pai
  useEffect(() => {
    // Considera válido apenas quando houver ao menos uma mensagem de texto no preview
    // e o usuário tiver decidido sobre mencionar todos (true)
    const temTextoPreview = mensagens.some(
      (m) => m.tipo === "texto" && m.conteudo && m.conteudo.trim() !== ""
    );
    const camposValidos = temTextoPreview && mencionarTodos === true;
    onValidacaoMensagem && onValidacaoMensagem(camposValidos); // Envia o estado de validação para o componente pai
  }, [mensagens, mencionarTodos, onValidacaoMensagem]);

  // Função para adicionar uma nova mensagem
  const handleAdicionarMensagem = () => {
    if (mensagem.trim()) {
      setMensagens((prevMensagens) => [
        ...prevMensagens,
        { tipo: "texto", conteudo: mensagem },
      ]);
      setMensagem(""); // Limpa o campo de texto após adicionar a mensagem
    }
  };

  // Ajustando botão Próximo para redirecionar para a próxima etapa
  const isNextButtonEnabled =
    mensagens.some(
      (m) => m.tipo === "texto" && m.conteudo && m.conteudo.trim() !== ""
    ) && mencionarTodos === true;

  const handleNextStep = () => {
    if (!isNextButtonEnabled) return;
    // Se o componente pai forneceu setEtapaAtual, use-o; caso contrário, tente onProximo
    if (typeof props.setEtapaAtual === "function") {
      // Vai para a etapa de Enviar (7)
      props.setEtapaAtual(7);
    } else if (typeof props.onProximo === "function") {
      props.onProximo();
    }
  };

  return (
    <div>
      {/* Botão Próximo */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
        <button
          style={{
            background: isNextButtonEnabled ? notifyColor : "#ccc", // Ativo apenas se as condições forem atendidas
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "12px 24px",
            fontWeight: "bold",
            fontSize: 16,
            cursor: isNextButtonEnabled ? "pointer" : "not-allowed", // Desabilita o clique se as condições não forem atendidas
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            transition: "background 0.3s",
          }}
          onClick={handleNextStep}
          disabled={!isNextButtonEnabled} // Desabilita o botão se as condições não forem atendidas
        >
          Próximo
        </button>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: props.isMobile ? "column" : "row",
          justifyContent: "center",
          gap: 24,
          width: "100%",
          alignItems: "flex-start",
          marginTop: 60,
        }}
      >
        {/* Área onde aparecem as mensagens */}
        <div
          style={{
            flex: 1,
            maxWidth: props.isMobile ? "100%" : "50%",
            background: "#f7f7f7",
            borderRadius: 12,
            padding: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {/* Conteúdo das mensagens */}
          <div
            style={{
              ...props.whatsappBg,
              height: 340,
              minHeight: 340,
              maxHeight: 340,
              padding: 20,
              marginLeft: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* Mensagens renderizadas */}
            <div
              style={{
                flex: 1,
                marginBottom: 24,
                maxHeight: 260,
                minHeight: 60,
                overflowY: "auto",
                width: "100%",
                paddingRight: 8,
              }}
            >
              {mensagens.length === 0 && (
                <div
                  style={{
                    color: "#888",
                    fontStyle: "italic",
                    fontSize: 16,
                  }}
                >
                  Sua mensagem aparecerá aqui após.
                </div>
              )}
              {mensagens.map((msg, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      background: "#dcf8c6",
                      borderRadius: "8px 8px 8px 8px",
                      padding: "8px 12px",
                      color: "#222",
                      fontSize: 15,
                      maxWidth: "75%",
                      minWidth: 50,
                      minHeight: 24,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      wordBreak: "break-word",
                      alignSelf: "flex-start",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    {/* Conteúdo da mensagem */}
                    {msg.tipo === "texto" ? (
                      <span style={{ flex: 1, whiteSpace: "pre-line" }}>
                        {renderComFormatacao(msg.conteudo)}
                      </span>
                    ) : msg.tipo === "enquete" ? (
                      <>
                        <strong>{msg.conteudo.pergunta}</strong>
                        <ul style={{ paddingLeft: 16 }}>
                          {msg.conteudo.opcoes.map((opcao, index) => {
                            const totalVotos = msg.conteudo.opcoes.reduce((sum, opcao) => sum + opcao.votos, 0);
                            const porcentagem = calcularPorcentagem(opcao.votos, totalVotos);

                            return (
                              <li key={index} style={{ marginBottom: 8 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span>{opcao.texto}</span>
                                </div>
                                <div
                                  style={{
                                    height: 8,
                                    background: "#e0e0e0",
                                    borderRadius: 4,
                                    overflow: "hidden",
                                    marginTop: 4,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${porcentagem}%`,
                                      height: "100%",
                                      background: "#51a197",
                                    }}
                                  ></div>
                                </div>
                                <span style={{ fontSize: 12, color: "#555" }}>{porcentagem}%</span>
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    ) : msg.tipo === "imagem" ? (
                      <img
                        src={msg.url}
                        alt="Imagem enviada"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 200,
                          borderRadius: 8,
                        }}
                      />
                    ) : msg.tipo === "video" ? (
                      <video
                        src={msg.url}
                        controls
                        style={{
                          maxWidth: "100%",
                          maxHeight: 200,
                          borderRadius: 8,
                        }}
                      />
                    ) : msg.tipo === "audio" ? (
                      <>
                        <AudioPlayer src={msg.url} />
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <FaEye style={{ color: "#888", fontSize: 14 }} />
                            <span style={{ fontSize: 12, color: "#888" }}>
                              {calcularPorcentagem(msg.visualizacoes, 100)}% visualizaram
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <FaMicrophone style={{ color: "#888", fontSize: 14 }} />
                            <span style={{ fontSize: 12, color: "#888" }}>
                              {calcularPorcentagem(msg.reproducoes, msg.visualizacoes)}% reproduziram
                            </span>
                          </div>
                        </div>
                      </>
                    ) : msg.tipo === "contato" ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          padding: "8px",
                          border: "1px solid #ccc",
                          borderRadius: 8,
                          background: "#f7f7f7",
                        }}
                      >
                        <strong>Nome: {msg.conteudo.nome}</strong>
                        <span>Número: {msg.conteudo.numero}</span>
                      </div>
                    ) : msg.tipo === "evento" ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          padding: "8px",
                          border: "1px solid #ccc",
                          borderRadius: 8,
                          background: "#f7f7f7",
                        }}
                      >
                        <strong>Título: {msg.conteudo.titulo}</strong>
                        <span>Data e Hora: {msg.conteudo.dataHora}</span>
                        <p>Descrição: {msg.conteudo.descricao}</p>
                      </div>
                    ) : msg.tipo === "arquivo" ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          padding: "8px",
                          border: "1px solid #ccc",
                          borderRadius: 8,
                          background: "#f7f7f7",
                        }}
                      >
                        <strong>Arquivo:</strong>
                        <a
                          href={msg.conteudo.url}
                          download={msg.conteudo.nome}
                          style={{
                            color: "#51a197",
                            textDecoration: "none",
                            fontWeight: "bold",
                          }}
                        >
                          {msg.conteudo.nome}
                        </a>
                      </div>
                    ) : null}

                    {/* Ícone de olho e contador de visualizações */}
                    {msg.tipo !== "audio" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 8 }}>
                        <FaEye style={{ color: "#888", fontSize: 14 }} />
                        <span style={{ fontSize: 12, color: "#888" }}>
                          {calcularPorcentagem(msg.visualizacoes, 100)}% visualizaram
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => handleEditarMensagem(idx)}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                      title="Editar"
                    >
                      <FaEdit style={{ color: "#ccc" }} />
                    </button>
                    <button
                      onClick={() => handleExcluirMensagem(idx)}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                      title="Excluir"
                    >
                      <FaTrash style={{ color: "#ccc" }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Ícones na parte inferior do campo de mensagens */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                marginTop: 16,
                justifyContent: "center",
              }}
            >
              <button
                style={props.notifyIconStyle}
                title="Áudio"
                onClick={() => setModalAudioAberto(true)}
                type="button"
              >
                <FaMicrophone />
              </button>
              {/* Ícone de vídeo */}
              <div>
                <input
                  id="upload-video"
                  type="file"
                  accept="video/mp4, video/webm, video/ogg"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const fileName = file.name.toLowerCase();
                      if (
                        fileName.endsWith(".mp4") ||
                        fileName.endsWith(".webm") ||
                        fileName.endsWith(".ogg")
                      ) {
                        const videoUrl = URL.createObjectURL(file);
                        setMensagens((prevMensagens) => [
                          ...prevMensagens,
                          { tipo: "video", url: videoUrl }, // Adiciona o vídeo como uma nova mensagem
                        ]);
                      } else {
                        alert("Apenas arquivos MP4, WEBM ou OGG são permitidos.");
                      }
                    }
                  }}
                />
                <button
                  style={props.notifyIconStyle}
                  title="Vídeo"
                  onClick={() => document.getElementById("upload-video").click()}
                  type="button"
                >
                  <FaVideo />
                </button>
              </div>
              {/* Ícone de imagem */}
              <div>
                <input
                  id="upload-imagem"
                  type="file"
                  accept="image/jpeg, image/png"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const fileName = file.name.toLowerCase();
                      if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg") || fileName.endsWith(".png")) {
                        const imageUrl = URL.createObjectURL(file);
                        setMensagens((prevMensagens) => [
                          ...prevMensagens,
                          { tipo: "imagem", url: imageUrl }, // Adiciona a imagem como uma nova mensagem
                        ]);
                      } else {
                        alert("Apenas arquivos JPG ou PNG são permitidos.");
                      }
                    }
                  }}
                />
                <button
                  style={props.notifyIconStyle}
                  title="Imagem"
                  onClick={() => document.getElementById("upload-imagem").click()}
                  type="button"
                >
                  <FaImage />
                </button>
              </div>
              <button style={props.notifyIconStyle} title="Enquete" onClick={() => setModalEnqueteAberto(true)}>
                <FaPoll />
              </button>
              <button style={props.notifyIconStyle} title="Contato" onClick={() => setModalContatoAberto(true)}>
                <FaAddressBook />
              </button>
              <button style={props.notifyIconStyle} title="Evento" onClick={() => setModalEventoAberto(true)}>
                <FaCalendarAlt />
              </button>
              <div>
                <input
                  id="upload-arquivo"
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const fileName = file.name;
                      const fileUrl = URL.createObjectURL(file);
                      setMensagens((prevMensagens) => [
                        ...prevMensagens,
                        { tipo: "arquivo", conteudo: { nome: fileName, url: fileUrl } }, // Adiciona o arquivo como uma nova mensagem
                      ]);
                    }
                  }}
                />
                <button
                  style={props.notifyIconStyle}
                  title="Arquivo"
                  onClick={() => document.getElementById("upload-arquivo").click()}
                >
                  <FaFileAlt />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Caixa de mensagem */}
        <div
          style={{
            flex: 1,
            maxWidth: props.isMobile ? "100%" : "50%",
            background: "#f7f7f7",
            borderRadius: 12,
            padding: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: 340,
          }}
        >
          <textarea
            rows={4}
            placeholder="Digite sua mensagem aqui..."
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            style={{
              borderRadius: 12,
              border: "1px solid #e0e0e0",
              padding: 12,
              fontSize: 16,
              color: "#333",
              resize: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              transition: "border-color 0.2s",
              width: "130%",
              height: isMobile ? "350px" : "100%", // Aumenta a altura no mobile
            }}
          />
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => {
                if (editando !== null) {
                  handleSalvarEdicao(); // Salva a edição da mensagem
                } else {
                  if (mensagem.trim() !== "") {
                    setMensagens((prevMensagens) => [
                      ...prevMensagens,
                      { tipo: "texto", conteudo: mensagem, visualizacoes: 0 }, // Adiciona visualizações
                    ]); // Adiciona uma nova mensagem
                    setMensagem(""); // Limpa o campo de texto
                  }
                }
              }}
              style={{
                background: props.notifyColor,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "8px 24px",
                fontWeight: "bold",
                fontSize: 16,
                cursor: "pointer",
                transition: "background 0.2s",
              }}
            >
              {editando !== null ? "Salvar Edição" : "Salvar"}
            </button>
            <button
              type="button"
              onClick={() => setMostrarEmojis((prev) => !prev)}
              style={{
                background: "#f7f7f7",
                border: "none",
                borderRadius: "50%",
                width: 40,
                height: 40,
                fontSize: 22,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: 4,
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              }}
              title="Inserir emoji"
            >
              <span role="img" aria-label="emoji">
                😊
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* BOX DE OPÇÕES (adicionado abaixo dos campos) */}
      <div
        style={{
          background: "#005c53",
          borderRadius: 14,
          padding: 20,
          color: "#fff",
          width: props.isMobile ? "100%" : 1300,
          marginTop: 24,
          marginLeft: props.isMobile ? 0 : 32,
          boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
          alignSelf: props.isMobile ? "stretch" : "flex-start",
        }}
      >
        {/* Mencionar todos os participantes */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: "bold", marginBottom: 8 }}>
            Mencionar todos os participantes
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button
              style={{
                background: mencionarTodos === true ? "#088b7eff" : "transparent",
                color: mencionarTodos === true ? "#fff" : "#f3f3f3ff",
                border: `2px solid #058073ff`,
                borderRadius: 8,
                padding: "6px 18px",
                fontWeight: "bold",
                fontSize: 15,
                cursor: "pointer",
                boxShadow:
                  mencionarTodos === true
                    ? "0 2px 8px rgba(0,92,83,0.15)"
                    : "none",
                transition: "background 0.2s, color 0.2s",
              }}
              onClick={() => setMencionarTodos(true)}
            >
              SIM
            </button>
            <button
              style={{
                background: mencionarTodos === false ? "#088b7eff" : "transparent",
                color: mencionarTodos === false ? "#fff" : "#f3f3f3ff",
                border: `2px solid #058073ff`,
                borderRadius: 8,
                padding: "6px 18px",
                fontWeight: "bold",
                fontSize: 15,
                cursor: "pointer",
                boxShadow:
                  mencionarTodos === false
                    ? "0 2px 8px rgba(0,92,83,0.15)"
                    : "none",
                transition: "background 0.2s, color 0.2s",
              }}
              onClick={() => setMencionarTodos(false)}
            >
              NÃO
            </button>
          </div>
        </div>

        {/* Ativar envio temporário */}
        <div style={{ marginBottom: "18px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
            Ativar envio temporário
          </div>
          <select
            value={envioTemporario}
            onChange={(e) => setEnvioTemporario(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 8,
              border: "none",
              fontSize: 15,
              fontWeight: "bold",
              color: "#005c53",
            }}
          >
            <option value="desativado">Desativado</option>
            <option value="24h">24h</option>
            <option value="7dias">7 dias</option>
            <option value="90dias">90 dias</option>
          </select>
        </div>

        {/* Velocidade */}
        <div>
          <div style={{ fontWeight: "bold", marginBottom: "8px" }}>Velocidade</div>
          <select
            value={velocidade}
            onChange={(e) => setVelocidade(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 8,
              border: "none",
              fontSize: 15,
              fontWeight: "bold",
              color: "#005c53",
            }}
          >
            <option value="nenhum">Nenhum</option>
            <option value="rapida">Rápida - 10s a 20s</option>
            <option value="normal">Normal - 40s a 60s</option>
            <option value="lenta">Lenta - 60s a 120s</option>
          </select>
        </div>
      </div>

      {/* Modal de gravação de áudio */}
      {modalAudioAberto && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: "90%",
              maxWidth: 400,
              textAlign: "center",
            }}
          >
            <h3 style={{ marginBottom: 16 }}>Gravação de Áudio</h3>

            {/* Ícone de microfone */}
            <FaMicrophone
              style={{
                fontSize: 48,
                color: notifyColor, // Cor da Notify
                marginBottom: 16,
              }}
            />

            {/* Botão para iniciar ou parar a gravação */}
            {gravandoAudio ? (
              <>
                <p>Gravando... {tempoGravacao}s</p>
                <button
                  onClick={pararGravacao}
                  style={{
                    background: "#e74c3c",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 16px",
                    cursor: "pointer",
                    marginTop: 16,
                  }}
                >
                  Parar Gravação
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={iniciarGravacao}
                  style={{
                    background: notifyColor,
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 16px",
                    cursor: "pointer",
                    marginTop: 16,
                  }}
                >
                  Gravar Áudio
                </button>
              </>
            )}

            {/* Opção para enviar arquivo MP3 */}
            <div style={{ marginTop: 16 }}>
              <label
                htmlFor="upload-audio"
                style={{
                  display: "inline-block",
                  background: "#ccc",
                  color: "#333",
                  borderRadius: 8,
                  padding: "8px 16px",
                  cursor: "pointer",
                }}
              >
                Enviar Arquivo MP3
              </label>
              <input
                id="upload-audio"
                type="file"
                accept="audio/mp3"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const fileName = file.name.toLowerCase();
                    if (fileName.endsWith(".mp3")) {
                      setAudioBlob(file);
                      setAudioUrl(URL.createObjectURL(file));
                      setAudioErro(""); // Limpa qualquer erro anterior
                    } else {
                      setAudioErro("Apenas arquivos MP3 são permitidos.");
                    }
                  }
                }}
              />
            </div>

            {/* Player de áudio e botão de envio */}
            {audioUrl && (
              <div style={{ marginTop: 16 }}>
                <audio controls src={audioUrl} />
                <button
                  onClick={handleEnviarAudio}
                  style={{
                    background: notifyColor,
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 16px",
                    cursor: "pointer",
                    marginTop: 16,
                  }}
                >
                  Enviar Áudio
                </button>
              </div>
            )}

            {/* Mensagem de erro */}
            {audioErro && (
              <p style={{ color: "red", marginTop: 8 }}>{audioErro}</p>
            )}

            {/* Botão para fechar o modal */}
            <button
              onClick={fecharModalAudio}
              style={{
                background: "#ccc",
                color: "#333",
                border: "none",
                borderRadius: 8,
                padding: "8px 16px",
                cursor: "pointer",
                marginTop: 16,
              }}
            >
              Fechar
            
            </button>
          </div>
        </div>
      )}

      {/* Modal de enquete */}
      {modalEnqueteAberto && (
        <Suspense fallback={<div>Carregando enquete...</div>}>
          <Enquete
            fecharEnquete={() => setModalEnqueteAberto(false)}
            enviarEnquete={handleEnviarEnquete}
          />
        </Suspense>
      )}

      {/* Modal de contato */}
      {modalContatoAberto && (
        <Suspense fallback={<div>Carregando contato...</div>}>
          <Contato
            fecharContato={() => setModalContatoAberto(false)}
            enviarContato={handleEnviarContato}
          />
        </Suspense>
      )}

      {/* Modal de evento */}
      {modalEventoAberto && (
        <Evento
          fecharEvento={() => setModalEventoAberto(false)} // Fecha o modal ao clicar no botão de fechar
          enviarEvento={(evento) => {
            setMensagens((prevMensagens) => [
              ...prevMensagens,
              { tipo: "evento", conteudo: evento }, // Adiciona o evento como uma nova mensagem
            ]);
            setModalEventoAberto(false); // Fecha o modal após enviar o evento
          }}
        />
      )}

      {mostrarEmojis && (
        <div
          style={{
            position: "absolute",
            bottom: 80,
            right: 20,
            zIndex: 1000,
          }}
        >
          <EmojiPicker
            onEmojiClick={(emojiData) => {
              setMensagem((prevMensagem) => prevMensagem + emojiData.emoji);
              setMostrarEmojis(false); // Fecha o seletor após selecionar um emoji
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Mensagem;